import React from 'react';
import styled, { useTheme } from 'styled-components';
import { LuInfo, LuTriangleAlert } from 'react-icons/lu';
import { Modal, ModalTitle, ModalBody, ModalFooter } from './Modal';
import { formatters } from '../services/api';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 8px;
  padding-right: 8px;
`;

const IconBadge = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const Title = styled(ModalTitle)`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
`;

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.6;
  color: #64748b;
`;

const InfoSection = styled.div`
  margin-bottom: 4px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #f8fafc;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.grayMedium || '#64748b'};
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  word-break: break-word;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f1f5f9;
  color: #475569;
`;

const TipNote = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Footer = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: stretch;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.35;
  background: ${props => props.theme.colors.gradientPrimary || 'linear-gradient(135deg, #1e3a8a 0%, rgb(19, 85, 154) 100%)'};
  color: ${props => props.theme.colors.white || '#ffffff'};
  box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  transition: all 0.3s ease;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: translateY(-1px);
  }
`;

const getVencimentoLabel = (charge) =>
  charge?.vencimento ||
  charge?.vencimentoFormatado ||
  (charge?.dataVencimento ? formatters.formatDate(charge.dataVencimento) : null);

const PriorOpenChargeModal = ({ isOpen, onContinue, onCancel, priorCharge, targetCharge }) => {
  const theme = useTheme();

  if (!priorCharge) return null;

  const dueLabel = getVencimentoLabel(priorCharge);
  const targetPeriod = targetCharge?.mesAno || 'esta cobrança';
  const priorPeriod = priorCharge.mesAno || 'Período não informado';
  const handleCancel = onCancel || onContinue;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      closeOnOverlayClick={false}
      showCloseButton={false}
    >
      <HeaderRow>
        <IconBadge>
          <LuTriangleAlert size={22} />
        </IconBadge>
        <TitleBlock>
          <Title>Cobrança anterior em aberto</Title>
        </TitleBlock>
      </HeaderRow>

      <ModalBody style={{ marginBottom: 0 }}>
        <Message>
          Existe uma cobrança de período anterior que ainda aparece em aberto no sistema.
          Verifique antes de pagar outra.
        </Message>

        <InfoSection>
          <SectionTitle>
            <LuInfo size={18} /> Cobrança em aberto
          </SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Mês/Ano</InfoLabel>
              <InfoValue>{priorPeriod}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Valor</InfoLabel>
              <InfoValue style={{ color: theme?.colors?.primary }}>
                {formatters.formatCurrency(priorCharge.valor)}
              </InfoValue>
            </InfoItem>
            {dueLabel && (
              <InfoItem>
                <InfoLabel>Vencimento</InfoLabel>
                <InfoValue>{dueLabel}</InfoValue>
              </InfoItem>
            )}
            {priorCharge.status && (
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <StatusBadge>{priorCharge.status}</StatusBadge>
                </InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        </InfoSection>

        <TipNote>
          <LuInfo size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            Se você já pagou por boleto, a baixa pode demorar um pouco para aparecer.
          </span>
        </TipNote>
      </ModalBody>

      <Footer>
        <ContinueButton type="button" onClick={onContinue}>
          Continuar para cobrança {targetPeriod}
        </ContinueButton>
        <CancelButton type="button" onClick={handleCancel}>
          Cancelar
        </CancelButton>
      </Footer>
    </Modal>
  );
};

export default PriorOpenChargeModal;
